import { useMemo } from 'react';
import { QueryOrigin } from '@fabric-ui/components';
import { generateOption } from '@fabric-ui/utils';
import { useQuery } from '@tanstack/react-query';
import { isValid } from 'date-fns';
import { useAvContext } from '../../../context/AvContextProvider';
import { useGetSourcesList } from '../../../hooks/useGetSourcesList';
import useQuerySql from '../../../hooks/useQuerySql';
import { isNullOrUndefined } from '../../../utils/utils';
import { getViewProjectionsFieldQuery } from '../../../views/Logs/get-view-projections-query';
import { LOGS_CONST } from '../../../views/Logs/Logs.const';
import { ANY_VALUE } from '../constants';

export const useGetValuesOptions = ({
  fieldName,
  enabled,
  timeRange,
}: {
  fieldName?: string | null;
  enabled: boolean;
  timeRange?: Date[];
}) => {
  const { api } = useAvContext();
  const { data: viewProjectionsFields, isLoading } = useQuery(getViewProjectionsFieldQuery({ api }));
  const viewProjectionId = viewProjectionsFields
    ? Object.keys(viewProjectionsFields).find(projId => projId && projId === fieldName?.split('.').at(0))
    : null;

  const isViewProjection = !!viewProjectionId;

  const isValidTime = useMemo(() => timeRange?.every(date => isValid(date)), [timeRange]);
  const sql = `select distinct ${fieldName} from ${isViewProjection ? viewProjectionId : LOGS_CONST.EXTERNAL_PROJECTION_TABLE} limit 100`;

  const sourcesQuery = useGetSourcesList();

  const valuesOptions = useQuerySql({
    sql,
    key: `${fieldName}${timeRange}`,
    origin: QueryOrigin.INTERACTIVE_QUERY,
    ...(timeRange
      ? {
          additionalFilter: {
            and: {
              operands: [
                {
                  expression: {
                    fieldName: isViewProjection ? `${viewProjectionId}.event_timestamp` : 'TIME',
                    dateCondition: { between: { values: timeRange } },
                  },
                },
              ],
            },
          },
        }
      : {}),
    options: {
      enabled: !isLoading && !!isValidTime && !!fieldName && fieldName !== ANY_VALUE && fieldName !== 'source' && enabled,
      gcTime: 0,
    },
    onSuccess: data =>
      data.reduce(
        (acc, { [fieldName!]: value }) =>
          (Array.isArray(value) ? value.length : !isNullOrUndefined(value))
            ? [...acc, generateOption(typeof value === 'object' ? JSON.stringify(value) : value.toString())]
            : acc,
        []
      ),
  });

  return {
    data:
      fieldName === 'source'
        ? sourcesQuery.data?.filter(s => !s.isFirstParty).map(s => generateOption(s.mappedSourceName))
        : valuesOptions.data,
    isLoading: fieldName === 'source' ? sourcesQuery.isLoading : valuesOptions.isLoading,
  };
};
